import { useState, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { cctvApi } from '@/api/cctv';
import { clientsApi } from '@/api/clients';
import type { CCTV as CCTVType } from '@/types';
import DataTable from '@/components/DataTable';
import SearchInput from '@/components/SearchInput';
import Pagination from '@/components/Pagination';
import { formatDate } from '@/utils/formatters';

const statusStyles: Record<string, string> = {
  active: 'text-green-600 bg-green-100',
  inactive: 'text-gray-600 bg-gray-100',
  maintenance: 'text-yellow-600 bg-yellow-100',
};

const statusLabels: Record<string, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  maintenance: 'Mantenimiento',
};

export function CCTV() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const limit = 10;

  const { data: response, isLoading, error } = useQuery({
    queryKey: ['cctv', page, search, statusFilter, clientFilter],
    queryFn: () => cctvApi.getAll({
      page,
      limit,
      search: search || undefined,
      status: statusFilter || undefined,
      clientId: clientFilter || undefined,
    }),
    placeholderData: keepPreviousData,
  });

  const { data: clientsResponse } = useQuery({
    queryKey: ['clients-for-cctv-filter'],
    queryFn: () => clientsApi.getAll({ limit: 200 }),
  });

  const cctvList: CCTVType[] = response?.data?.data || [];
  const totalPages = response?.data?.totalPages || 1;
  const clients = clientsResponse?.data?.data || [];

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  };

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setClientFilter(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      key: 'brand',
      label: 'Marca',
      sortable: true,
      render: (item: CCTVType) => item.brand || '-',
    },
    {
      key: 'model',
      label: 'Modelo',
      sortable: true,
      render: (item: CCTVType) => item.model || '-',
    },
    {
      key: 'client',
      label: 'Cliente',
      render: (item: CCTVType) => item.client?.name || '-',
    },
    {
      key: 'location',
      label: 'Ubicación',
      render: (item: CCTVType) => item.location || '-',
    },
    {
      key: 'status',
      label: 'Estado',
      sortable: true,
      render: (item: CCTVType) => {
        const color = statusStyles[item.status] || 'text-gray-600 bg-gray-100';
        const label = statusLabels[item.status] || item.status;
        return (
          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Instalación',
      sortable: true,
      render: (item: CCTVType) => formatDate(item.createdAt),
    },
  ];

  const errorMessage = error
    ? typeof error === 'string'
      ? error
      : 'Error al cargar equipos CCTV'
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-surface-900">Equipos CCTV</h1>
        <button
          type="button"
          onClick={() => navigate('/cctv/new')}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nuevo Equipo
        </button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="w-full sm:max-w-xs">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Buscar equipos..."
          />
        </div>
        <select
          value={statusFilter}
          onChange={handleStatusChange}
          className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="inactive">Inactivo</option>
          <option value="maintenance">Mantenimiento</option>
        </select>
        <select
          value={clientFilter}
          onChange={handleClientChange}
          className="rounded-md border border-surface-300 bg-white px-3 py-2 text-sm text-surface-700 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">Todos los clientes</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      <DataTable<CCTVType>
        columns={columns}
        data={cctvList}
        loading={isLoading}
        error={errorMessage}
        emptyMessage="No hay equipos CCTV registrados"
        onRowClick={(item) => navigate(`/cctv/${item.id}`)}
      />

      {!isLoading && cctvList.length > 0 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}

export default CCTV;
